/* eslint-disable react/prop-types */
import React from 'react';
import {
  Grid,
  useBreakpointValue,
  GridItem,
  Box,
  Flex,
  Text,
  Spinner,
} from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { sideBarRoutes } from 'utils/StaticData';
import PropTypes from 'prop-types';
import { errorsColor } from 'utils/StaticData';

const RecurringuserGraph = ({
  adminDetails,
  errorOverviewData,
  isErrorLoading,
}) => {
  const { errorsByStation = [], errorCounts = [] } = errorOverviewData || {};
  let errorlabales = [];

  if (errorsByStation?.length) {
    errorlabales = Object.keys(errorsByStation[0]).filter(
      (e) => e !== 'station',
    );
  }

  const CustomTooltipForRecurring = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const label =
        adminDetails?.role !== sideBarRoutes?.Admin ? (
          <p>Station: {data.name}</p>
        ) : (
          <p>Merchant: {data.name}</p>
        );
      return (
        <div
          className="custom-tooltip"
          style={{
            background: '#fff',
            padding: '10px',
            border: '1px solid #ccc',
          }}
        >
          {label}
          <p>Count: {data.count}</p>
        </div>
      );
    }
    return null;
  };

  CustomTooltipForRecurring.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
  };
  const innerRadius = useBreakpointValue({
    base: 80,
    sm: 100,
    lg: 90,
    xl: 70,
    '2xl': 100,
  });
  const outerRadius = useBreakpointValue({
    base: 110,
    sm: 130,
    lg: 120,
    xl: 100,
    '2xl': 130,
  });
  return (
    <>
      <Grid
        bg="white"
        p={{ base: '4', md: '5' }}
        templateColumns="repeat(12, 1fr)"
        gap={{ base: '16px', lg: '24px' }}
        boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
        borderRadius="20px"
        border="1px solid #D9D9D9"
        w="full"
      >
        {/* Station Data Graph */}
        <GridItem colSpan={{ base: '12', md: '6', xl: '8' }}>
          <Box
            w="full"
            h="full"
            p={{ base: '4', md: '5' }}
            bg="white"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
            borderRadius="20px"
            border="1px solid #D9D9D9"
          >
            <Flex justify="space-between" align="baseline" mb={4}>
              <Text
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight="semibold"
                color="primary"
              >
                Failures Per Station
              </Text>
              {!isErrorLoading && (
                <Flex flexWrap="wrap" align="left" flexDirection="column">
                  {errorlabales?.map((item, index) => (
                    <Flex
                      key={index}
                      align={{ base: 'flex-start', lg: 'center' }}
                      ml="4"
                    >
                      <Box
                        w={{ base: '8px', lg: '10px' }}
                        h={{ base: '8px', lg: '10px' }}
                        bg={errorsColor[index]}
                        borderRadius="50%"
                        mt={{ base: '4px', lg: '0' }}
                        mr="2"
                        flexShrink="0"
                      />
                      <Text fontSize={{ base: '10px', md: '12px' }}>
                        {item}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>

            {isErrorLoading ? (
              <>
                <Flex justify="center" alignItems="center" height="300px">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              </>
            ) : (
              <>
                {errorsByStation?.length > 0 ? (
                  <Box overflowX="auto" className="custom-scrollbar">
                    <Box
                      minWidth={`${Math.max(errorsByStation.length * 50, 600)}px`}
                    >
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={errorsByStation}
                          margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
                          barCategoryGap={
                            errorsByStation?.length < 5 ? '10%' : '20%'
                          }
                          barGap={0}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            horizontal={true}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="station"
                            interval={0}
                            tick={{
                              angle: errorsByStation?.length < 5 ? 0 : -45,
                              textAnchor:
                                errorsByStation?.length < 5 ? 'middle' : 'end',
                              fontSize: 12,
                            }}
                          />
                          <YAxis
                            allowDecimals={false}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip />
                          {errorlabales?.map((e, i) => {
                            const CustomBarShape = (props) => {
                              // eslint-disable-next-line react/prop-types
                              const { x, y, width, height, station } = props;
                              const customWidth =
                                errorsByStation?.length < 5
                                  ? errorsByStation?.length === 1
                                    ? 60
                                    : errorsByStation?.length === 2
                                      ? 60
                                      : errorsByStation?.length === 3
                                        ? 50
                                        : 40
                                  : width;

                              // eslint-disable-next-line no-mixed-operators
                              const centeredX = x + (width - customWidth) / 2;

                              const activeErrors = errorlabales.filter(
                                (error) =>
                                  errorsByStation.find(
                                    (s) => s.station === station,
                                  )?.[error] > 0,
                              );

                              const isTopBar =
                                activeErrors[activeErrors.length - 1] === e;

                              if (!isTopBar) {
                                return (
                                  <rect
                                    x={centeredX}
                                    y={y}
                                    width={customWidth}
                                    height={height}
                                    fill={errorsColor[i]}
                                  />
                                );
                              }

                              const radius = 5;
                              return (
                                <path
                                  d={`M${centeredX},${y + radius} 
                            Q${centeredX},${y} ${centeredX + radius},${y}
                            L${centeredX + customWidth - radius},${y}
                            Q${centeredX + customWidth},${y} ${centeredX + customWidth},${y + radius}
                            L${centeredX + customWidth},${y + height}
                            L${centeredX},${y + height}
                            Z`}
                                  fill={errorsColor[i]}
                                />
                              );
                            };

                            return (
                              <Bar
                                key={e}
                                dataKey={e}
                                fill={errorsColor[i]}
                                name={e}
                                stackId="a"
                                shape={<CustomBarShape />}
                              />
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                ) : (
                  <Flex justify="center" alignItems="center" height="300px">
                    <Text fontSize="18px" fontWeight="bold" color="gray.500">
                      No records available
                    </Text>
                  </Flex>
                )}
              </>
            )}
          </Box>
        </GridItem>
        {/* Decline reason Graph */}
        <GridItem colSpan={{ base: '12', md: '6', xl: '4' }}>
          <Box
            w="full"
            h="full"
            p="5"
            bg="white"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
            borderRadius="20px"
            border="1px solid #D9D9D9"
          >
            <Text
              fontSize={{ base: '16px', md: '24px' }}
              fontWeight="semibold"
              color="primary"
            >
              Split of Decline Reasons
            </Text>
            {isErrorLoading ? (
              <>
                <Flex justify="center" alignItems="center" height="300px">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              </>
            ) : (
              <>
                {errorCounts?.length > 0 ? (
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                    className="chart_round"
                  >
                    <PieChart className="pie-chart-outer">
                      <Pie
                        data={errorCounts}
                        cx="50%"
                        cy="50%"
                        startAngle={90}
                        endAngle={-270}
                        // innerRadius={100} // Adjust inner radius
                        // outerRadius={140} // Increase size for better visibility

                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        dataKey="count"
                      >
                        {errorCounts?.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={errorsColor[index]}
                          />
                        ))}
                      </Pie>
                      {/* tootip */}
                      <Tooltip
                        formatter={(value, name) => [`${value}`, name]}
                        contentStyle={{
                          backgroundColor: '#fff',
                          padding: '8px',
                          boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
                        }}
                      />

                      {/* Center Number Label */}
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="36px"
                        fontWeight="bold"
                        fill="#0F2C3E"
                      >
                        {errorCounts.reduce((sum, item) => sum + item.count, 0)}
                      </text>
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Flex justify="center" align="center" height="300px">
                    <Text fontSize="18px" fontWeight="bold" color="gray.500">
                      No records available
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {/* Legend */}
            {!isErrorLoading && (
              <Flex mt="4" justify="center" flexWrap="wrap">
                {errorlabales?.map((item, index) => (
                  <Flex key={index} alignItems="center" mx="2">
                    <Box
                      w="10px"
                      h="10px"
                      bg={errorsColor[index]}
                      borderRadius="50%"
                      mr="2"
                    />
                    <Text fontSize="12px">{item}</Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

RecurringuserGraph.propTypes = {
  adminDetails: PropTypes.object,
  showTooltip: PropTypes.bool,
  setShowTooltip: PropTypes.func,
  errorOverviewData: PropTypes.any,
  recurringUsers: PropTypes.any,
};

export default RecurringuserGraph;
